"use client";

import { RotatingText } from "@/app/components/ui/rotating-text";

import { useEffect, useState } from "react";
import profoundLogo from "../../../images/profound-logo.svg";

interface TransitionLoadingOverlayProps {
	onComplete: () => void;
}

const loadingMessages = [
	"Analyzing your conversation...",
	"Extracting your strengths...",
	"Identifying key milestones...",
	"Finalizing profile insights...",
];

export function TransitionLoadingOverlay({
	onComplete,
}: TransitionLoadingOverlayProps) {
	const [isVisible, setIsVisible] = useState(true);

	useEffect(() => {
		// Total duration: 4 messages × (1.5s display + 0.8s transition) = 9.2 seconds
		const messageDuration = 1500; // 1.5 seconds per message
		const transitionDuration = 800; // 0.8 second transition
		const cycleTime = messageDuration + transitionDuration;
		const totalDuration = loadingMessages.length * cycleTime;

		const completionTimer = setTimeout(() => {
			setIsVisible(false);
			onComplete();
		}, totalDuration);

		return () => {
			clearTimeout(completionTimer);
		};
	}, [onComplete]);

	return (
		<div
			className={`fixed inset-0 flex flex-col items-center justify-center z-50 transition-opacity duration-300 ${
				isVisible ? "opacity-100" : "opacity-0"
			}`}
			style={{
				background:
					"linear-gradient(180deg, #F8F4C5 0%, #E4C5FF 49.52%, #F8F4C5 100%)",
			}}
		>
			{/* Icon */}
			<div className="mb-8 sm:mb-12">
				<div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-[#10333B] rounded-full flex items-center justify-center">
					<div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 relative">
						<img
							src={profoundLogo}
							alt="Profound Logo"
							className="object-contain p-2 w-full h-full"
							style={{ filter: "brightness(0) invert(1)" }}
						/>
					</div>
				</div>
			</div>

			{/* Loading message with RotatingText */}
			<h2
				className="text-2xl sm:text-3xl md:text-4xl mb-3 sm:mb-4 text-[#1D1D1D] text-center px-4"
				style={{
					fontFamily: "Playfair Display, serif",
					fontStyle: "italic",
					fontWeight: 400,
				}}
			>
				<RotatingText
					text={loadingMessages}
					duration={1500}
					transition={{ duration: 0.8, ease: "ease-in-out" }}
				/>
			</h2>

			{/* Subtitle */}
			<p
				className="text-base sm:text-lg text-gray-700 text-center px-4"
				style={{
					fontFamily: "Inter, sans-serif",
					fontWeight: 400,
				}}
			>
				This may take a moment
			</p>
		</div>
	);
}
